import { useEffect, useState } from 'react';
import { Plus, MoreHorizontal, Pencil, Trash2, Unlock, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  PageHeader,
  SearchInput,
  DataTable,
  Column,
  StatusBadge,
  ConfirmDialog,
} from '@/components/common';
import { usersApi, User, QueryUserParams } from '@/api/services';
import { getErrorMessage } from '@/api/client';

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; user?: User }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = async (params?: QueryUserParams) => {
    try {
      setLoading(true);
      const response = await usersApi.getAll(params);
      setUsers(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers({ search: searchQuery || undefined });
  }, [searchQuery]);

  const handleDelete = async () => {
    if (!deleteDialog.user) return;

    try {
      setDeleting(true);
      await usersApi.delete(deleteDialog.user.id);
      toast.success('User deactivated successfully');
      setDeleteDialog({ open: false });
      fetchUsers({ search: searchQuery || undefined });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const handleUnlock = async (user: User) => {
    try {
      await usersApi.unlockAccount(user.id);
      toast.success('Account unlocked successfully');
      fetchUsers({ search: searchQuery || undefined });
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const columns: Column<User>[] = [
    {
      key: 'fullName',
      header: 'User',
      sortable: true,
      render: (user) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-primary/10 text-primary text-xs">
              {getInitials(user.fullName)}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{user.fullName}</div>
            <div className="text-sm text-muted-foreground">{user.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      render: (user) => (
        <span className="text-muted-foreground">
          {user.department || '-'}
        </span>
      ),
    },
    {
      key: 'userRoles',
      header: 'Roles',
      render: (user) => (
        <div className="flex flex-wrap gap-1">
          {user.userRoles.slice(0, 2).map((ur) => (
            <Badge key={ur.id} variant="secondary" className="text-xs">
              {ur.role.name}
            </Badge>
          ))}
          {user.userRoles.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{user.userRoles.length - 2}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user) => (
        <StatusBadge status={user.status.toLowerCase() as any} />
      ),
    },
    {
      key: 'lastLoginAt',
      header: 'Last Login',
      render: (user) =>
        user.lastLoginAt
          ? new Date(user.lastLoginAt).toLocaleDateString()
          : 'Never',
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (user) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Key className="mr-2 h-4 w-4" />
              Reset Password
            </DropdownMenuItem>
            {user.status === 'LOCKED' && (
              <DropdownMenuItem onClick={() => handleUnlock(user)}>
                <Unlock className="mr-2 h-4 w-4" />
                Unlock Account
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, user })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Deactivate
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="p-6">
      <PageHeader
        title="Users"
        description="Manage user accounts and permissions"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Users' },
        ]}
        actions={
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        }
      />

      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search users..."
          className="max-w-sm"
        />
      </div>

      <DataTable
        data={users}
        columns={columns}
        loading={loading}
        emptyTitle="No users found"
        emptyDescription="Get started by inviting your first user."
        emptyAction={
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        }
      />

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        title="Deactivate User"
        description={`Are you sure you want to deactivate "${deleteDialog.user?.fullName}"? They will no longer be able to access the system.`}
        confirmLabel="Deactivate"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleting}
      />
    </div>
  );
}
